import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { endOfDay, startOfDay } from 'date-fns';
import { map, Observable } from 'rxjs';
import {
    IHistoricalConversations,
    IHistoricalConversation,
    IMessageHistory,
    IEmailMessageHistory,
    ICustomerDetails,
    IConversationQueues,
    IConversationSummary,
} from '@dxp/core/models';
import {
    ChannelType,
    Direction,
    MediaGroup,
    LOGGER_REQUEST_NAME,
} from '@dxp/core/types';
import { environment } from '@dxp/core/environment';
import { toIsoStringIgnoreTimezone } from '@dxp/core/helpers';

@Injectable({
    providedIn: 'root',
})
export class ConversationApiService {
    readonly #http = inject(HttpClient);

    getConversationHistoryByCustomer(
        customerId: string,
        count: boolean,
        expand: string[],
        skip: number,
        top: number,
        filters: {
            agents?: string[];
            channels?: number[];
            channelTypes?: ChannelType[];
            direction?: Direction;
            completionCodes?: string[];
            date?: { from: Date | null; to: Date | null };
        },
        orderBy: string,
    ): Observable<IHistoricalConversations> {
        const customerFilter = `(CustomerId eq '${customerId}')`;
        const noVirtualChannel = "(channelType ne 'virtual')";
        const agentFilter = filters.agents?.length
            ? `(workItems/any(workitems:((workitems/agentId in (${filters.agents.map(id => `'${id}'`).join(',')})))))`
            : undefined;
        const channelFilter = filters.channels?.length
            ? `(channelId in (${filters.channels.join(',')}))`
            : undefined;
        const channelTypesFilter = filters.channelTypes?.length
            ? `(channelType in (${filters.channelTypes.map(id => `'${id}'`).join(',')}))`
            : undefined;
        const completionCodesFilter = filters.completionCodes?.length
            ? `(workItems/any(workitems:((workitems/completionCode in (${filters.completionCodes.map(id => `'${id}'`).join(',')})))))`
            : undefined;

        let dateFilter = undefined;

        if (filters.date?.from && filters.date?.to) {
            const from = toIsoStringIgnoreTimezone(
                startOfDay(filters.date.from),
            );
            const to = toIsoStringIgnoreTimezone(endOfDay(filters.date.to));

            dateFilter = `(startedAtUtc gt ${from} and startedAtUtc lt ${to})`;
        }

        const filter = [
            customerFilter,
            noVirtualChannel,
            agentFilter,
            channelFilter,
            channelTypesFilter,
            filters.direction === 'OutBound'
                ? '(isOutbound eq true)'
                : undefined,
            filters.direction === 'InBound'
                ? '(isOutbound eq false)'
                : undefined,
            completionCodesFilter,
            dateFilter,
        ]
            .filter(x => !!x)
            .join(' and ');

        const params = new HttpParams()
            .set('$count', count.toString())
            .set('$filter', filter)
            .set('$expand', expand.join(','))
            .set('$skip', skip.toString())
            .set('$top', top.toString())
            .set('$orderBy', orderBy);

        return this.#http
            .get<{
                '@odata.count': number;
                value: IHistoricalConversation[];
            }>(`${environment.odataBaseUrl}/v1/conversations`, {
                params,
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Conversation Service (Get conversations by customer)',
                ),
            })
            .pipe(
                map(response => {
                    return {
                        count: response['@odata.count'],
                        historicalConversations: response.value,
                    };
                }),
            );
    }

    getConversationMessagingActivityHistory(
        conversationId: string,
    ): Observable<IMessageHistory[]> {
        return this.#http.get<IMessageHistory[]>(
            `${environment.contentV2Url}/conversationhistory?conversation_id=${conversationId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Conversation Service (Get conversation message history)',
                ),
            },
        );
    }

    getConversationEmailActivityHistory(
        conversationId: string,
    ): Observable<IEmailMessageHistory[]> {
        return this.#http.get<IEmailMessageHistory[]>(
            `${environment.contentV2Url}/messages/email/byconversationid?conversationId=${conversationId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Conversation Service (Get conversation email history)',
                ),
            },
        );
    }

    makeCustomerActive(
        customer: ICustomerDetails,
        conversationId: string,
    ): Observable<void> {
        return this.#http.post<void>(
            `${environment.workflowUrl}/api/conversation/${conversationId}/setData`,
            [
                {
                    key: 'CustomerId',
                    value: customer.customerId,
                },
            ],
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Conversation Service (Set customer active)',
                ),
            },
        );
    }

    getConversationQueues(
        conversationId: string,
    ): Observable<IConversationQueues[]> {
        return this.#http.get<IConversationQueues[]>(
            `${environment.workflowUrl}/conversations/${conversationId}/queues`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Conversation Service (Get conversation queues)',
                ),
            },
        );
    }

    getConversationSummary(
        conversationId: string,
        media: MediaGroup,
    ): Observable<IConversationSummary> {
        return this.#http.get<IConversationSummary>(
            `${environment.contentUrl}/conversations/${conversationId}/summary?mediaGroup=${media}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Conversation Service (Get conversation summary)',
                ),
            },
        );
    }
}
